/**
 * Location data aggregation utilities
 * Aggregates building and room data to calculate availability and status
 */

/**
 * Check if a room is currently within opening hours
 * @param {Object} building - Building object with openingHours data
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Date} currentTime - Current time
 * @returns {boolean} True if room is currently open
 */
export const isBuildingCurrentlyOpen = (building, date, currentTime) => {
	const openingHours = building.openingHours?.[date];

	if (!openingHours?.sessions || openingHours.sessions.length === 0) {
		return false;
	}

	return openingHours.sessions.some((session) => {
		// Parse times properly - API returns mixed time zones
		const startTime = new Date(session.startTime);
		const endTime = new Date(session.endTime);

		// For debugging the specific case we saw:
		// startTime: "2025-08-20T08:00:00+02:00" -> 06:00 UTC
		// endTime: "2025-08-20T16:00:00+00:00" -> 16:00 UTC
		// currentTime: 18:39 UTC (20:39 CEST)
		// The room is actually closed (16:00 UTC = 18:00 CEST)

		return currentTime >= startTime && currentTime < endTime;
	});
};

/**
 * Check if a room is currently available (open and not booked)
 * @param {Object} room - Room object with bookings and openingHours
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Date} currentTime - Current time
 * @returns {boolean} True if room is available right now
 */
export const isRoomCurrentlyAvailable = (building, room, date, currentTime) => {
	// First check if room is open
	if (!isBuildingCurrentlyOpen(building, date, currentTime)) {
		return false;
	}

	// Check if there's a current booking
	if (!room.bookings || room.bookings.length === 0) {
		return true;
	}

	return !room.bookings.some((booking) => {
		const startTime = new Date(booking.startTime);
		const endTime = new Date(booking.endTime);
		return currentTime >= startTime && currentTime < endTime;
	});
};

/**
 * Get the next available time for a room
 * @param {Object} building - Building object with openingHours
 * @param {Object} room - Room object with bookings
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Date} currentTime - Current time
 * @returns {Date|null} Next available time or null if none found
 */
export const getNextAvailableTime = (building, room, date, currentTime) => {
	const openingHours = building.openingHours?.[date];
	if (!openingHours?.sessions || openingHours.sessions.length === 0) {
		// Room is closed today, find next opening
		return getNextOpeningTime(building, date, currentTime);
	}

	// If room is currently available, return current time
	if (isRoomCurrentlyAvailable(building, room, date, currentTime)) {
		return currentTime;
	}

	// Find next gap between bookings or end of current booking
	const sortedBookings = (room.bookings || [])
		.map((booking) => ({
			start: new Date(booking.startTime),
			end: new Date(booking.endTime),
		}))
		.sort((a, b) => a.start - b.start);

	// Check for gaps between bookings within opening hours
	for (const session of openingHours.sessions) {
		const sessionStart = new Date(session.startTime);
		const sessionEnd = new Date(session.endTime);

		if (currentTime > sessionEnd) continue;

		const relevantBookings = sortedBookings.filter(
			(booking) => booking.start < sessionEnd && booking.end > sessionStart
		);

		let nextAvailable = Math.max(currentTime, sessionStart);

		for (const booking of relevantBookings) {
			if (nextAvailable < booking.start) {
				return new Date(nextAvailable);
			}
			nextAvailable = Math.max(nextAvailable, booking.end);
		}

		if (nextAvailable < sessionEnd) {
			return new Date(nextAvailable);
		}
	}

	// No availability today, find next opening
	return getNextOpeningTime(building, date, currentTime);
};

/**
 * Get the next opening time for a building (next day)
 * @param {Object} building - Building object with openingHours
 * @param {string} date - Current date
 * @param {Date} currentTime - Current time
 * @returns {Date|null} Next opening time or null
 */
export const getNextOpeningTime = (building, date, currentTime) => {
	// Check tomorrow first
	const tomorrow = new Date(currentTime);
	tomorrow.setDate(tomorrow.getDate() + 1);
	const tomorrowDateString = tomorrow.toISOString().split("T")[0];

	const tomorrowHours = building.openingHours?.[tomorrowDateString];
	if (tomorrowHours?.sessions && tomorrowHours.sessions.length > 0) {
		return new Date(tomorrowHours.sessions[0].startTime);
	}

	// Check if room has opening hours today - if so, it probably opens tomorrow too
	const todayHours = building.openingHours?.[date];
	if (todayHours?.sessions && todayHours.sessions.length > 0) {
		// Use the same opening time as today
		const todayStart = new Date(todayHours.sessions[0].startTime);
		const standardOpen = new Date(tomorrow);
		standardOpen.setHours(todayStart.getHours(), todayStart.getMinutes(), 0, 0);
		return standardOpen;
	}

	return null;
};

/**
 * Get building status based on room availability
 * @param {Object} building - Building object with openingHours
 * @param {Array} rooms - Array of room objects
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Date} currentTime - Current time
 * @returns {Object} Building status with openRoomsCount, availableRoomsCount, isOpen
 */
export const getBuildingStatus = (building, rooms, date, currentTime) => {
	let openRoomsCount = 0;
	let availableRoomsCount = 0;

	for (const room of rooms) {
		if (isBuildingCurrentlyOpen(building, date, currentTime)) {
			openRoomsCount++;
			if (isRoomCurrentlyAvailable(building, room, date, currentTime)) {
				availableRoomsCount++;
			}
		}
	}

	return {
		openRoomsCount,
		availableRoomsCount,
		isOpen: openRoomsCount > 0,
	};
};

/**
 * Get next available time for any room in a building
 * @param {Array} rooms - Array of room objects
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Date} currentTime - Current time
 * @returns {Date|null} Next available time for any room in building
 */
export const getBuildingNextAvailable = (rooms, date, currentTime) => {
	const nextTimes = rooms
		.map((room) => getNextAvailableTime(room, date, currentTime))
		.filter((time) => time !== null)
		.sort((a, b) => a - b);

	return nextTimes[0] || null;
};

/**
 * Get today's opening hours for display
 * @param {Object} building - Building object with openingHours data
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {string} Formatted opening hours string
 */
export const getTodaysOpeningHours = (building, date) => {
	// Collect all opening hours for today across all rooms
	const todaysSessions = [];
	const openingHours = building.openingHours?.[date];
	if (openingHours?.sessions && openingHours.sessions.length > 0) {
		for (const session of openingHours.sessions) {
			const startTime = new Date(session.startTime);
			const endTime = new Date(session.endTime);
			todaysSessions.push({ start: startTime, end: endTime });
		}
	}

	if (todaysSessions.length === 0) {
		return "Heute geschlossen";
	}

	// Find the earliest start and latest end time
	const earliestStart = new Date(Math.min(...todaysSessions.map(s => s.start)));
	const latestEnd = new Date(Math.max(...todaysSessions.map(s => s.end)));

	const startStr = earliestStart.toLocaleTimeString('de-DE', {
		hour: '2-digit',
		minute: '2-digit',
		timeZone: 'Europe/Berlin'
	});

	const endStr = latestEnd.toLocaleTimeString('de-DE', {
		hour: '2-digit',
		minute: '2-digit',
		timeZone: 'Europe/Berlin'
	});

	// Check for 24-hour opening (00:00 - 00:00 or similar patterns)
	if ((startStr === '00:00' && endStr === '00:00') ||
	    (startStr === '00:00' && endStr === '23:59')) {
		return "Heute ganztägig geöffnet";
	}

	// Special case for midnight end times that should be next day
	let displayEndStr = endStr;
	if (endStr === '00:00' && latestEnd > earliestStart) {
		// This is likely a 24:00 time that got converted to 00:00 of next day
		displayEndStr = '24:00';
	}

	return `Heute zwischen ${startStr} - ${displayEndStr} Uhr geöffnet`;
};

/**
 * Format time for display - simplified to just show open/closed status
 * @param {Date} time - Time to format (unused in simplified version)
 * @param {Date} currentTime - Current time for comparison (unused in simplified version)
 * @param {boolean} isCurrentlyOpen - Whether the building is currently open
 * @param {boolean} hasOpeningHours - Whether the building has any opening hours configured
 * @returns {string} Formatted time string
 */
export const formatTimeForDisplay = (
	time,
	currentTime,
	isCurrentlyOpen = false,
	hasOpeningHours = true
) => {
	// Simplified logic: just show open or closed status
	return isCurrentlyOpen ? "Heute geöffnet" : "Heute geschlossen";
};

/**
 * Process raw API data into location objects for the LocationsTable
 * @param {Object} masterData - Master data from API
 * @param {Object} bookingsData - Bookings data from API
 * @param {Date} currentTime - Current time
 * @returns {Array} Processed location objects
 */
export const processLocationData = (
	masterData,
	bookingsData,
	currentTime = new Date()
) => {
	if (!masterData?.buildings?.list || !bookingsData?.buildings) {
		return [];
	}

	const today = currentTime.toISOString().split("T")[0];
	const locations = [];

	for (const [buildingId, building] of Object.entries(
		masterData.buildings.list
	)) {
		// Get booking data for this building
		const buildingBookingData = bookingsData.buildings[buildingId];

		if (!buildingBookingData?.rooms) {
			continue;
		}

		// Process rooms data
		const rooms = building.rooms.map((room) => {
			const roomBookingData = buildingBookingData.rooms[room.id];
			return {
				...room,
				bookings: roomBookingData?.bookings || [],
			};
		});

		// Calculate building status
		const status = getBuildingStatus(buildingBookingData, rooms, today, currentTime);

        // Get today's opening hours for display
        const todaysHours = getTodaysOpeningHours(buildingBookingData, today);
        
        // Check if building has opening hours today (regardless of room availability)
        const hasOpeningHoursToday = isBuildingCurrentlyOpen(buildingBookingData, today, currentTime) ||
            (buildingBookingData.openingHours?.[today]?.sessions?.length > 0);

		locations.push({
			id: building.id,
			name: building.name,
			short: building.short,
			address: building.address,
			planUrl: building.planUrl,
			images: building.images,
			availableRooms: status.availableRoomsCount,
			openingHours: hasOpeningHoursToday ? "open" : "closed",
			nextAvailable: todaysHours,
			isOpen: hasOpeningHoursToday,
			totalRooms: rooms.length,
			openRooms: status.openRoomsCount,
		});
	}

	return locations;
};
